import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight, Instagram } from 'lucide-react'
import { LogoMark } from '@/components/generals/logo'
import { TrialButton } from '@/components/generals/trial-button'
import { SectionBadge } from '@/components/landing/section-badge'
import { LinkedinMark, XMark } from '@/components/generals/brand-icons'
import { siteConfig } from '@/constants/site'

const LINK_GROUPS = [
    {
        title: 'Product',
        links: [
            { label: 'How it works', href: '/#workflow' },
            { label: 'Oversight', href: '/#oversight' },
            { label: 'Dashboard', href: '/#performance' },
            { label: 'Savings', href: '/#value' }
        ]
    },
    {
        title: 'Legal',
        links: [
            { label: 'Privacy', href: '/legal#privacy' },
            { label: 'Cookies', href: '/legal#cookies' },
            { label: 'Terms of use', href: '/legal#terms' }
        ]
    }
]

const SOCIAL_LINKS = [
    { label: 'Fieldhand on X', href: siteConfig.links?.x, icon: <XMark className='size-4' /> },
    { label: 'Fieldhand on Instagram', href: siteConfig.links?.instagram, icon: <Instagram aria-hidden className='size-5' strokeWidth={2} /> },
    { label: 'Fieldhand on LinkedIn', href: siteConfig.links?.linkedin, icon: <LinkedinMark className='size-4' /> }
]

const linkClass = 'group inline-flex items-center gap-1 rounded-md text-[16px] text-ink/70 transition-colors hover:text-moss focus-visible:ring-2 focus-visible:ring-moss focus-visible:outline-none'

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='bg-white px-3 pt-6 pb-3 sm:px-4 sm:pb-4'>
            <div className='mx-auto w-full max-w-[1320px] rounded-[32px] bg-mist px-6 pt-6 sm:rounded-[44px] sm:px-8 sm:pt-8'>
                <div className='ticket relative overflow-hidden rounded-[26px] bg-meadow px-6 py-10 [--notch:17px] sm:rounded-[34px] sm:px-12 sm:py-14 sm:[--notch:22px]'>
                    <div className='flex flex-col gap-8 md:flex-row md:items-end md:justify-between'>
                        <div>
                            <SectionBadge className='bg-white/60'>Start today</SectionBadge>

                            <p className='mt-5 max-w-[18ch] font-heading text-[clamp(2rem,3.6vw,3rem)] leading-[1.1] tracking-[-0.03em] text-balance text-ink'>
                                Put your first agent to <em className='italic text-moss-deep'>work</em> this week.
                            </p>
                        </div>

                        <TrialButton source='footer' className='h-14 self-start px-7 text-[17px] md:self-auto'>
                            Start your free trial
                            <ArrowUpRight aria-hidden className='size-5' />
                        </TrialButton>
                    </div>
                </div>

                <div className='grid gap-12 px-2 py-12 sm:grid-cols-2 sm:py-16 lg:grid-cols-[1.4fr_1fr_1fr] lg:px-6'>
                    <div className='sm:col-span-2 lg:col-span-1'>
                        <Link
                            href='/'
                            aria-label='Fieldhand home'
                            className='inline-flex items-center gap-2.5 rounded-xl text-ink focus-visible:ring-2 focus-visible:ring-moss focus-visible:ring-offset-4 focus-visible:ring-offset-mist focus-visible:outline-none'
                        >
                            <LogoMark className='h-8' />
                            <span className='font-heading text-[28px] leading-none font-medium tracking-[-0.035em]'>
                                fieldhand
                            </span>
                        </Link>

                        <p className='mt-5 max-w-[36ch] text-[16px] leading-relaxed text-ink/65'>
                            AI agents that take on the repetitive work, with your team watching over every step.
                        </p>

                        <ul className='mt-7 flex items-center gap-3'>
                            {SOCIAL_LINKS.filter((social) => social.href).map((social) => (
                                <li key={social.label}>
                                    <a
                                        href={social.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        aria-label={social.label}
                                        className='flex size-11 items-center justify-center rounded-full bg-white text-ink shadow-[0_8px_20px_-14px_rgba(20,23,15,0.4)] transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-sage focus-visible:ring-2 focus-visible:ring-moss focus-visible:outline-none'
                                    >
                                        {social.icon}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {LINK_GROUPS.map((group) => (
                        <nav key={group.title} aria-label={group.title}>
                            <p className='text-[14px] font-medium text-ink'>
                                {group.title}
                            </p>

                            <ul className='mt-5 space-y-3.5'>
                                {group.links.map((link) => (
                                    <li key={link.label}>
                                        <Link href={link.href} className={linkClass}>
                                            {link.label}
                                            <ArrowUpRight aria-hidden className='size-4 opacity-0 transition-opacity group-hover:opacity-100' />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    ))}
                </div>

                <div className='flex flex-col gap-3 border-t border-ink/10 px-2 py-7 text-[14px] text-ink/55 sm:flex-row sm:items-center sm:justify-between lg:px-6'>
                    <p>© 2026 Fieldhand. All rights reserved.</p>
                    <p>Built for teams who would rather do the work that matters.</p>
                </div>
            </div>
        </footer>
    )
}
